import Vue from 'utils/vueUtils';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import MessagesReassign from '../collections/MessagesReassign';
import MessagesSimple from '../collections/MessagesSimple';
import CannedMessages from '../collections/CannedMessages';
import MediaCollection from '../collections/MediaCollection';
import MessageSimple from '../models/MessageSimple';
import MessageUndo from '../models/MessageUndo';
import { cloneDeep } from 'lodash';
import { baseApi } from 'core/api';
import { useConversationsStore, useInboxesStore, useMessageFormStore } from 'store/modules';

const state = () => ({
    _order: [],
    _videoConverted: false,
    _orderOrigin: [],
    _item_id: null,
    _items: {},
    _itemsParams: {
        limit: 20,
    },
    _itemsPagination: {
        hasPreviousMore: false,
        hasNextMore: false,
    },
    _itemsLoading: false,
    _itemsLoadingMore: false,
    _itemsLoadingForConversation: false,
    _itemsFailed: false,
    _cannedItems: [],
    _cannedPagination: {
        limit: 7,
        currentPage: 1,
        hasNextPage: true,
        hasSearch: false,
        total: 0,
    },
    _reassignItems: [],
    _undoItems: {},
    _simpleItems: [],
    _simpleItemsLoading: false,
    _simpleItemsLoadingMore: false,
    _simpleItemsParams: {
        page: 1,
    },
    _simpleItemsPagination: {
        hasMore: false,
    },
    _isSignatureActive: false,
    _signatureMessage: '',
});

const getters = {
    messageActive: (state) => state._item_id,
    messagesOrder: (state) => state._order,
    messagesOrderNotReassign: (state) => state._order.filter((id) => !id.includes('reassign')),
    messagesOrderOrigin: (state) => state._orderOrigin,
    messagesItems: (state) => state._items,
    messagesItemsArray: (state) => {
        const array = [];

        for (let key in state._items) {
            array.push(state._items[key]);
        }

        return array;
    },
    messagesParams(state) {
        return {
            ...state._itemsParams,
            limit: this.messageActive ? 200 : 20,
        };
    },
    messagesPagination: (state) => state._itemsPagination,
    messagesLoading: (state) => state._itemsLoading,
    messagesLoadingMore: (state) => state._itemsLoadingMore,
    messagesLoadingForConversation: (state) => state._itemsLoadingForConversation,
    messagesFailed: (state) => state._itemsFailed,
    messagesCanned: (state) => state._cannedItems,
    messagesUndo: (state) => state._undoItems,
    messagesUndoById: (state) => (id) => state._undoItems[id],
    paginationCanned: (state) => state._cannedPagination,
    videoConverted: (state) => state._videoConverted,
    messagesSimple: (state) => state._simpleItems,
    messagesSimpleLoading: (state) => state._simpleItemsLoading,
    messagesSimpleLoadingMore: (state) => state._simpleItemsLoadingMore,
    messagesSimpleParams: (state) => state._simpleItemsParams,
    messagesSimplePagination: (state) => state._simpleItemsPagination,
    isSignatureActive: (state) => state._isSignatureActive,
    signatureMessage: (state) => state._signatureMessage,
};

const utils = {
    datesItems: (state) => {
        const orderReceived = [];
        const orderSend = [];

        state._order.forEach((id, i, array) => {
            const item = state._items[id];
            const prevItem = state._items[array[i - 1]];

            if (!prevItem) {
                item.statusCurrentDate = true;
            } else {
                const prevDate = moment(prevItem.created_at).local().dayOfYear();
                const currentDate = moment(item.created_at).local().dayOfYear();

                if (prevDate === currentDate) {
                    item.statusCurrentDate = false;
                } else {
                    item.statusCurrentDate = true;
                }
            }

            state._items[id] = item;
        });

        state._orderOrigin.forEach((id) => {
            const item = state._items[id];

            if (item.hasScheduled) {
                item.statusCurrentMinutesInfo = true;
                item.statusCurrentMinutesAvatar = true;
            } else {
                if (item.received_at) {
                    orderReceived.push(id);
                } else {
                    orderSend.push(id);
                }
            }

            state._items[id] = item;
        });

        const group = (id, i, array) => {
            const item = state._items[id];
            const prevItem = state._items[array[i - 1]];
            const nextItem = state._items[array[i + 1]];
            const currentMinutes = item.createdTimestampMinute;

            if (!prevItem) {
                if (nextItem) {
                    const nextMinutes = nextItem.createdTimestampMinute;

                    if (currentMinutes === nextMinutes) {
                        nextItem.statusCurrentMinutesInfo = false;
                    } else {
                        item.statusCurrentMinutesInfo = true;
                    }
                } else {
                    item.statusCurrentMinutesInfo = true;
                }

                item.statusCurrentMinutesAvatar = true;
            } else {
                const prevMinutes = prevItem.createdTimestampMinute;

                if (nextItem) {
                    const nextMinutes = nextItem.createdTimestampMinute;

                    if (currentMinutes === prevMinutes && prevItem.contact.id === item.contact.id) {
                        item.statusCurrentMinutesAvatar = false;
                        nextItem.statusCurrentMinutesAvatar = false;
                    } else {
                        item.statusCurrentMinutesAvatar = true;
                    }

                    if (currentMinutes === nextMinutes) {
                        item.statusCurrentMinutesInfo = false;
                        nextItem.statusCurrentMinutesInfo = false;
                    } else {
                        item.statusCurrentMinutesInfo = true;
                    }

                    state._items[array[i + 1]] = nextItem;
                } else {
                    if (currentMinutes === prevMinutes && prevItem.contact.id === item.contact.id) {
                        item.statusCurrentMinutesAvatar = false;
                    } else {
                        item.statusCurrentMinutesAvatar = true;
                    }

                    item.statusCurrentMinutesInfo = true;
                }
            }

            if (item.hasScheduled) {
                item.statusCurrentMinutesInfo = true;
                item.statusCurrentMinutesAvatar = true;
            }

            if (prevItem && prevItem.hasScheduled) {
                prevItem.statusCurrentMinutesInfo = true;
                prevItem.statusCurrentMinutesAvatar = true;

                state._items[array[i - 1]] = prevItem;
            }

            if (nextItem && nextItem.isEmptyTranscript) {
                item.statusCurrentMinutesInfo = true;
            }

            if (nextItem && nextItem.hasScheduled) {
                nextItem.statusCurrentMinutesInfo = true;
                nextItem.statusCurrentMinutesAvatar = true;

                state._items[array[i + 1]] = nextItem;
            }

            state._items[id] = item;
        };

        orderReceived.map(group);
        orderSend.map(group);
    },

    reassignItems: (state) => {
        let showStatus = false;
        const messagesSimpleCount = Object.keys(state._items).filter((id) => {
            return state._items[id] instanceof MessageSimple;
        }).length;
        const messagesCount = Object.keys(state._items).length;

        state._reassignItems.forEach((item) => {
            const id = item.idReassign;

            state._items[id] = item;

            if (!state._order.includes(item.idReassign)) {
                state._order.push(item.idReassign);
            }
        });

        state._order = Object.keys(state._items)
            .sort((current, next) => {
                const messageCurrent = state._items[current];
                const messageNext = state._items[next];

                if (
                    messageCurrent.constructor.name === 'MessageReassign' &&
                    messageNext.constructor.name === 'MessageSimple' &&
                    messageCurrent.messageTimestamp === messageNext.messageTimestamp
                ) {
                    return -1;
                }

                return messageCurrent.messageTimestamp - messageNext.messageTimestamp;
            })
            .map((id) => {
                if (
                    state._items[id].type !== 'reassign' ||
                    !messagesSimpleCount ||
                    !(messagesCount >= 20)
                ) {
                    showStatus = true;
                }

                if (state._items[id].type === 'reassign') {
                    state._items[id].showMessage = showStatus;
                }

                return id;
            });
    },
};

const actions = {
    messageReassignLogs(items) {
        this._setMessagesReassignLogs(items);
    },

    getMessagesByConversationId({ conversationId, cancelToken, messageId, hasPrev, hasNext }) {
        const options = {
            params: {
                ...this.messagesParams,
                grouped_response: true,
            },
        };
        const hasMore = hasPrev || hasNext;

        // active message
        if (messageId) {
            options.params.afterOrEqual = messageId;
            options.params.offsetAfter = 10;
        }

        if (hasPrev) {
            options.params.before = this.messagesOrderNotReassign[0];
        }

        if (hasNext) {
            options.params.after =
                this.messagesOrderNotReassign[this.messagesOrderNotReassign.length - 1];
        }

        if (cancelToken) {
            options.cancelToken = cancelToken;
        }

        if (hasMore) {
            this._setMessageStateUpdate({ itemsLoadingMore: true });
        } else {
            this._setMessageStateUpdate({ itemsLoading: true });
        }

        return baseApi
            .get(`core/messages/${conversationId}`, options)
            .then((response) => {
                const data = response.data;
                const contacts = data.contacts;
                const users = data.users;
                const hasNextMore = data.hasNext;
                const hasPreviousMore = data.hasPrevious;
                const messages = data.messages.map((item) => {
                    item.contact = contacts[item.contact_id] || null;
                    item.user = users[item.user_id] || null;

                    return item;
                });

                const items = new MessagesSimple(messages).models;

                if (hasMore) {
                    this._setMessagesMoreLoaded({
                        response: {
                            messages: items,
                            hasPreviousMore: hasPreviousMore,
                            hasNextMore: hasNextMore,
                        },
                        props: {
                            hasPrev: hasPrev,
                            hasNext: hasNext,
                        },
                    });
                } else {
                    this._setMessagesLoaded({
                        response: {
                            messages: items,
                            hasNextMore: hasNextMore,
                            hasPreviousMore: hasPreviousMore,
                        },
                    });
                }

                return items;
            })
            .catch((e) => {
                this._setMessagesFailed();
            })
            .finally(() => {
                if (hasMore) {
                    this._setMessageStateUpdate({
                        itemsLoadingMore: false,
                    });
                } else {
                    this._setMessageStateUpdate({
                        itemsLoading: false,
                        item_id: null,
                    });
                }
            });
    },

    getMessagesContacts({ params, cancelToken, hasPrev, hasNext }) {
        const contactsIds = params?.contacts;
        const teamId = params?.team_id;

        if (!teamId) return;
        if (!contactsIds?.length) return;

        const options = {
            params: {
                ...this.messagesParams,
                ...params,
            },
        };
        if (cancelToken) {
            options.cancelToken = cancelToken;
        }

        // Default endpoint without pagination for group messages
        const endpointConfig = {
            endpoint: 'core/messages/contacts',
            isGroup: true,
        };

        const hasMore = hasPrev || hasNext;

        if (hasMore) {
            this._setMessageStateUpdate({ itemsLoadingMore: true });
        } else {
            this._setMessageStateUpdate({ itemsLoading: true });
        }

        // Get messages with pagination for 1 contact
        if (options.params.contacts.length === 1) {
            const contactId = options.params.contacts[0];
            const teamId = options.params.team_id;

            endpointConfig.endpoint = `core/messages/contacts/${contactId}/teams/${teamId}`;
            endpointConfig.isGroup = false;

            // Clear options
            delete options.params.contacts;
            delete options.params.team_id;

            // Update params to get pagination
            options.params.grouped_response = true;

            if (hasPrev) {
                options.params.before = this.messagesOrderNotReassign[0];
            }

            if (hasNext) {
                options.params.after =
                    this.messagesOrderNotReassign[this.messagesOrderNotReassign.length - 1];
            }
        }

        return baseApi
            .get(endpointConfig.endpoint, options)
            .then((response) => {
                if (endpointConfig.isGroup) {
                    const messages = new MessagesSimple(response.data).models;

                    this._setMessagesLoaded({
                        response: {
                            messages: messages,
                            hasNextMore: false,
                            hasPreviousMore: false,
                        },
                    });

                    return messages;
                } else {
                    const data = response.data;
                    const contacts = data.contacts;
                    const users = data.users;
                    const hasNextMore = data.hasNext;
                    const hasPreviousMore = data.hasPrevious;

                    if (data.conversation?.number_id) {
                        const inbox = useInboxesStore().inbox;

                        if (inbox && inbox.isSmart) {
                            const number = inbox.numbers.find(
                                ({ id }) => id === data.conversation?.number_id
                            );
                            if (number) {
                                const conversation = cloneDeep(
                                    useConversationsStore().conversation
                                );
                                conversation.update({ number });

                                useConversationsStore()._setConversationUpdate({
                                    conversation,
                                    updateOrder: false,
                                    updateProps: ['number'],
                                });
                            }
                        }
                    }

                    const messages =
                        data?.messages?.length > 0
                            ? data.messages.map((item) => {
                                  item.contact = contacts[item.contact_id] || null;
                                  item.user = users[item.user_id] || null;
                                  return item;
                              })
                            : [];

                    const items = new MessagesSimple(messages).models;

                    this._setMessagesMoreLoaded({
                        response: {
                            messages: items,
                            hasPreviousMore: hasPreviousMore,
                            hasNextMore: hasNextMore,
                        },
                        props: {
                            hasPrev,
                            hasNext,
                        },
                    });

                    return items;
                }
            })
            .catch(() => {
                this._setMessagesFailed();
            })
            .finally(() => {
                if (hasMore) {
                    this._setMessageStateUpdate({
                        itemsLoadingMore: false,
                    });
                } else {
                    this._setMessageStateUpdate({
                        itemsLoading: false,
                        item_id: null,
                    });
                }
            });
    },

    getMessagesSimple({ filter = 'scheduled', inbox_id, inbox_type, hasMore = false } = {}) {
        if (hasMore) {
            this._setMessageStateUpdate({
                simpleItemsLoadingMore: true,
            });
        } else {
            this._setMessageStateUpdate({
                simpleItemsLoading: true,
            });
        }

        let params = {
            ...this._simpleItemsParams,
            inbox_id: inbox_id,
            inbox_type: inbox_type,
        };

        if (filter === 'scheduled') {
            params.scheduled = true;
        }

        return baseApi
            .get(`core/messages/${filter}`, {
                params: params,
            })
            .then((response) => {
                const data = response.data;

                this._setMessagesSimpleLoaded({
                    data: data,
                    filter: filter,
                });

                return data;
            })
            .finally(() => {
                this._setMessageStateUpdate({
                    simpleItemsLoading: false,
                    simpleItemsLoadingMore: false,
                });
            });
    },

    async getMessagesSimplePage({ page, filter, inbox_id, inbox_type } = {}) {
        this._setMessageStateUpdate({
            simpleItemsParams: {
                ...this._simpleItemsParams,
                page: page,
            },
        });

        await this.getMessagesSimple({
            filter: filter,
            inbox_id: inbox_id,
            inbox_type: inbox_type,
            hasMore: true,
        });
    },

    messagesReset() {
        this._setMessagesReset();
    },

    createMessagesById({ form, id }) {
        return form.post(`core/messages/${id}`).then((response) => {
            return response;
        });
    },

    messageSent(data) {
        this._setMessageSent(data);
    },

    messageReceived(message) {
        if (this.conversation.id !== message.conversation_id) return;

        this._setMessageReceived(message);
    },

    messageDeleted(id) {
        if (!this._items[id]) return;

        this._setMessageDeleted(id);
    },

    deleteMessagesById({ id, form }) {
        return form.delete(`core/messages/${id}`).then((response) => {
            this._setMessageDeleted(id);

            return response;
        });
    },

    getMessagesDraft({ id, inboxId, inboxType }) {
        return baseApi
            .get(`core/messages/draft/${id}`, {
                params: {
                    inbox_id: inboxId,
                    inbox_type: inboxType,
                },
            })
            .then((response) => {
                return response.data;
            });
    },

    deleteMessagesDraft({ form, isAjax = true, isAxios = false, id, params = {} } = {}) {
        if (!isAjax) {
            this._setMessageDeletedDraft(id);
            useMessageFormStore()._setMessageFormRemoveCachedMessage(id);

            return;
        }

        if (isAxios) {
            return baseApi
                .delete('core/messages/draft', {
                    params: params,
                })
                .then((response) => {
                    this._setMessageDeletedDraft(params.conversation_id);
                    useMessageFormStore()._setMessageFormRemoveCachedMessage(
                        params.conversation_id
                    );

                    return response;
                });
        }

        return form.deleteWithBody('core/messages/draft').then((response) => {
            this._setMessageDeletedDraft(form.conversation_id);
            useMessageFormStore()._setMessageFormRemoveCachedMessage(form.conversation_id);

            return response;
        });
    },

    updateMessagesById({ id, form }) {
        return form.put(`core/messages/${id}`).then((response) => {
            this._setMessageUpdated(response.data);

            return response.data;
        });
    },

    messageUpdated(data) {
        if (!this._items[data.id]) {
            if (data.sent_at) {
                this._setMessageSent(data);
            } else {
                this._setMessageReceived(data);
            }

            return;
        }

        this._setMessageUpdated(data);
    },

    messageUpdatedProps({ id, props }) {
        this._setMessageUpdatedProps({ id, props });
    },

    getCannedMessages(params) {
        return baseApi.get('core/canned-messages', { params }).then((response) => {
            this._setMessageCanned(response.data);

            return response.data;
        });
    },

    resetCannedMessages() {
        this._setMessageCannedReset();
    },

    resetCannedMessagesPagination(search = false) {
        this._setMessageCannedResetPagination(search);
    },

    videoWasConverted(value) {
        this._setMediaLibraryVideoConverted(value);
    },

    updateMessagesFilesUpload({ formData, cancelToken, onUploadProgress = () => {} }) {
        return baseApi
            .post('core/messages/files/upload', formData, {
                cancelToken: cancelToken,
                onUploadProgress: onUploadProgress,
            })
            .then((response) => {
                return response.data.urls;
            });
    },

    updateAttachmentsUpload({ formData, cancelToken, onUploadProgress = () => {} }) {
        return baseApi
            .post('core/attachments/upload', formData, {
                cancelToken: cancelToken,
                onUploadProgress: onUploadProgress,
                baseURL: '/',
            })
            .then((response) => {
                return response.data;
            })
            .finally(() => {
                useMessageFormStore().getAttachmentsList();
            });
    },

    updateAttachmentsConvert({ formData, cancelToken, onUploadProgress = () => {} }) {
        return baseApi
            .post('core/attachments/convert', formData, {
                cancelToken: cancelToken,
                onUploadProgress: onUploadProgress,
            })
            .then((response) => {
                return response.data;
            })
            .finally(() => {
                useMessageFormStore().getAttachmentsList();
            });
    },

    getAttachmentsPreview({ id, data }) {
        return baseApi
            .get('core/attachments/preview', {
                params: data,
            })
            .then((response) => {
                if (Array.isArray(response.data)) {
                    const previews = response.data.map((item) => ({
                        id: uuidv4(),
                        ...item,
                    }));

                    this._setMessagePreview({
                        id: id,
                        previews: new MediaCollection(previews).models,
                    });
                }

                return Array.isArray(response.data) ? response.data : [];
            });
    },

    deleteAttachments({ params }) {
        return baseApi
            .delete('core/attachments', {
                params: params,
            })
            .then((response) => {
                return response.data;
            });
    },

    updateAttachmentsDownload({ params }) {
        return baseApi
            .post('core/attachments/download', params, { responseType: 'blob' })
            .then((response) => {
                return response;
            });
    },

    updateMessagesByIdRetry({ id }) {
        return baseApi.post(`core/messages/${id}/retry`).then((response) => {
            return response.data;
        });
    },

    updateMessagesByIdMentionsByIdActivate({ messageId, userId }) {
        this._setMessageUpdateMention({
            type: 'active',
            action: 'loading',
        });

        return baseApi
            .post(`core/messages/${messageId}/mentions/${userId}/activate`)
            .then((response) => {
                this._setMessageUpdateMention({
                    message: new MessageSimple(response.data),
                    messageId: messageId,
                    membersId: userId,
                    type: 'active',
                    action: 'loaded',
                });

                return response.data;
            });
    },

    updateMessagesByIdMentionsByIdCancel({ messageId, userId }) {
        this._setMessageUpdateMention({
            type: 'cancel',
            action: 'loading',
        });

        return baseApi
            .post(`core/messages/${messageId}/mentions/${userId}/cancel`)
            .then((response) => {
                this._setMessageUpdateMention({
                    message: new MessageSimple(response.data),
                    messageId: messageId,
                    membersId: userId,
                    type: 'cancel',
                    action: 'loaded',
                });

                return response.data;
            });
    },

    updateMessagesByIdMentionsByIdGotIt({ messageId, userId }) {
        this._setMessageUpdateMention({
            type: 'got_it',
            action: 'loading',
        });

        return baseApi
            .post(`core/messages/${messageId}/mentions/${userId}/got-it`)
            .then((response) => {
                this._setMessageUpdateMention({
                    message: new MessageSimple(response.data),
                    messageId: messageId,
                    membersId: userId,
                    type: 'got_it',
                    action: 'loaded',
                });

                return response.data;
            });
    },

    setMessageActive(id) {
        this._setMessageActive(id);
    },

    updateMessagesByIdMediaRetry(id) {
        return baseApi.post(`core/messages/${id}/media/retry`).then((response) => {
            return response.data;
        });
    },

    getKioskMessagesUrl(url) {
        return baseApi.get(`core/kiosk/messages/${url}`).then((response) => {
            return response.data;
        });
    },

    updateStateMessages(data = {}) {
        this._setMessageStateUpdate(data);
    },

    addUndoMessages(item) {
        this._setMessageAddUndo(item);
    },

    deleteUndoMessages(item) {
        this._setMessageDeleteUndo(item);
    },

    updateUndoMessages({ id, props } = {}) {
        this._setMessageUpdateUndo({ id, props });
    },

    resetMessagesSimple() {
        this._setMessagesSimpleReset();
    },

    updateSignatureState({ signature, status }) {
        this._setMessageUpdateSignatureMessage({
            message: signature,
            status: status,
        });

        return baseApi
            .post('core/users/message-signature', {
                signature: signature,
                is_active: status,
            })
            .then((response) => {
                return response.data;
            });
    },

    getSignatureMessage() {
        return baseApi.get('core/users/message-signature').then((response) => {
            if (Object.keys(response.data).length) {
                this._setMessageUpdateSignatureMessage({
                    message: response.data.signature,
                    status: response.data.is_active,
                });
            }

            return response.data;
        });
    },

    getTranscribeMessage(messageId) {
        return baseApi.get(`core/messages/${messageId}/transcript`).then((response) => {
            const message = response.data;

            if (this.conversation.id === message.conversation_id) {
                this._setMessageReceived(message);
            }

            return response.data;
        });
    },

    retryTranscript(message) {
        if (this.conversation.id === message.conversation_id) {
            this._setMessageResetStatus(message.id);
        }

        return baseApi.post(`core/messages/${message.id}/transcript/retry`);
    },

    _setMessagesSimpleReset() {
        this._simpleItems = [];
        this._simpleItemsPagination = {};
        this._simpleItemsParams = {
            page: 1,
        };
    },

    _setMessagesSimpleLoaded({ data, filter }) {
        if (filter === 'draft') {
            if (!this._simpleItems.length) {
                this._simpleItems = data.data;
            } else {
                data.data.forEach((item) => {
                    this._simpleItems.push(item);
                });
            }
        }

        if (filter === 'scheduled') {
            if (!this._simpleItems.length) {
                this._simpleItems = new MessagesSimple(data.data).models;
            } else {
                new MessagesSimple(data.data).models.forEach((item) => {
                    this._simpleItems.push(item);
                });
            }
        }

        this._simpleItemsPagination = {
            current_page: data.current_page,
            from: data.from,
            last_page: data.last_page,
            per_page: data.per_page,
            to: data.to,
            total: data.total,
            hasMore: Boolean(data.next_page_url),
        };
    },

    _setMessageUpdateUndo({ id, props }) {
        let item = this._undoItems[id];

        if (item) {
            for (let key in props) {
                item[key] = props[key];
            }

            Vue.set(this._undoItems, id, item);
        }
    },

    _setMessageDeleteUndo(item) {
        if (!item) return;

        Vue.delete(this._undoItems, item.conversation.id);
    },

    _setMessageAddUndo(item) {
        if (this._undoItems[item.conversation.id]) return;

        const form = item.form;
        const mediaBody = [...form.video_url, ...form.docs_url];
        const mediaUrl = form.media_url.map((media) => ({
            content_type: media.content_type,
            source: media.url,
        }));

        let body = item.signature ? form.message + '<br>\n<br>\n' + item.signature : form.message;

        mediaBody.forEach((item) => {
            if (!body) {
                body = item.source_short;
            } else {
                body = body + ' ' + item.source_short;
            }
        });

        const message = new MessageUndo({
            ...item,
            body: body,
            body_raw: body,
            media: mediaUrl,
            previews: [],
            created_at: moment.utc().format('YYYY-MM-DD hh:mm:ss'),
            status: 'faked',
        });

        Vue.set(this._undoItems, item.conversation.id, message);
    },

    _setMessageStateUpdate(data) {
        for (let key in data) {
            this[`_${key}`] = data[key];
        }
    },

    _setMessageUpdatedProps({ id, props }) {
        let message = this._items[id];

        if (message) {
            message.handleUpdateProps(props);

            Vue.set(this._items, id, message);
        }
    },

    _setMessageUpdateMention({ messageId, membersId, action, type }) {
        for (let key in this._items) {
            const message = this._items[key];

            if (message.pending_mentions.length) {
                if (action === 'loading') {
                    message.processing = true;

                    Vue.set(this._items, message.id, message);
                }

                if (action === 'loaded') {
                    if (type === 'active') {
                        message.processing = false;
                        message.pending_mentions = message.pending_mentions.filter(
                            (item) => item.id !== membersId
                        );

                        Vue.set(this._items, message.id, message);
                    }

                    if (type === 'cancel') {
                        message.processing = false;

                        if (message.id === messageId) {
                            message.pending_mentions = message.pending_mentions.filter(
                                (item) => item.id !== membersId
                            );
                        }

                        Vue.set(this._items, message.id, message);
                    }

                    if (type === 'got_it') {
                        message.processing = false;

                        if (message.id === messageId) {
                            Vue.set(this._items, message.id, message);
                        }
                    }
                }
            }
        }
    },

    _setMessageActive(id) {
        this._item_id = id;
    },

    _setMessagePreview({ id, previews }) {
        const message = this._items[id];
        const messageUndo = this._undoItems[id];

        if (message) {
            message.previews = previews;

            Vue.set(this._items, id, message);
        }

        if (messageUndo) {
            messageUndo.previews = previews;

            Vue.set(this._undoItems, id, messageUndo);
        }
    },

    _setMessagesReassignLogs(items) {
        items = new MessagesReassign(items);

        this._reassignItems = items.models;

        utils.reassignItems(this);
        utils.datesItems(this);
    },

    _setMessageCannedResetPagination(search) {
        this._cannedPagination = {
            currentPage: 1,
            hasNextPage: true,
            hasSearch: search,
        };
    },

    _setMediaLibraryVideoConverted(value) {
        this._videoConverted = value;
    },

    _setMessageCannedReset() {
        const cannedMessages = new CannedMessages([]);

        this._cannedItems = cannedMessages.models;
    },

    _setMessageCanned(response) {
        const cannedMessages = new CannedMessages(response.data || []);

        if (!this._cannedItems.length || this._cannedPagination.hasSearch) {
            this._cannedItems = cannedMessages.models;
        } else {
            let cannedItems = cannedMessages.models.filter((canned) => {
                const cannedExist = this._cannedItems.find((item) => item.id === canned.id);

                if (!cannedExist) {
                    return true;
                }

                return false;
            });

            this._cannedItems = this._cannedItems.concat(cannedItems);
        }

        this._cannedPagination = {
            currentPage: response.current_page,
            hasNextPage: Boolean(response.next_page_url),
            hasSearch: false,
            total: response.total,
        };
    },

    _setMessagesLoaded({ response }) {
        const { messages, hasPreviousMore, hasNextMore } = response;
        const order = [];
        const items = messages.reduce((items, item) => {
            items[item.id] = item;
            order.unshift(item.id);
            return items;
        }, {});

        this._order = order;
        this._orderOrigin = order;
        this._items = items;

        utils.reassignItems(this);
        utils.datesItems(this);

        this._itemsPagination.hasNextMore = hasNextMore;
        this._itemsPagination.hasPreviousMore = hasPreviousMore;
        this._itemsFailed = false;
    },

    _setMessagesMoreLoaded({ response, props }) {
        const { messages, hasPreviousMore, hasNextMore } = response;
        const { hasNext } = props;

        messages.forEach((item) => {
            this._items[item.id] = item;

            if (hasNext) {
                this._order.push(item.id);
                this._orderOrigin.push(item.id);
            } else {
                this._order.unshift(item.id);
                this._orderOrigin.unshift(item.id);
            }
        });

        utils.reassignItems(this);
        utils.datesItems(this);

        this._itemsPagination.hasNextMore = hasNextMore;
        this._itemsPagination.hasPreviousMore = hasPreviousMore;
        this._itemsFailed = false;
    },

    _setMessagesFailed() {
        this._itemsFailed = true;
    },

    _setMessagesReset() {
        this._order = [];
        this._orderOrigin = [];
        this._items = {};
        this._dates = [];
        this._reassignItems = [];
        this._itemsFailed = false;
        this._itemsPagination.hasMore = false;
        this._itemsLoading = false;
        this._itemsLoadingMore = false;
    },

    _setMessageSent(data) {
        if (this._items[data.id]) {
            data.previews = this._items[data.id].previews;
        }

        Vue.set(this._items, data.id, new MessageSimple(data));

        if (!this._order.includes(data.id)) {
            this._order.push(data.id);
            this._orderOrigin.push(data.id);
        }

        utils.reassignItems(this);
        utils.datesItems(this);
    },

    _setMessageReceived(data) {
        if (this._items[data.id]) {
            data.previews = this._items[data.id].previews;
        }
        Vue.set(this._items, data.id, new MessageSimple(data));

        if (!this._order.includes(data.id)) {
            this._order.push(data.id);
            this._orderOrigin.push(data.id);
        }

        utils.reassignItems(this);
        utils.datesItems(this);
    },

    _setMessageDeleted(id) {
        this._order = this._order.filter((o) => o != id);
        this._orderOrigin = this._order.filter((o) => o != id);
        Vue.delete(this._items, id);
        Vue.delete(
            this._simpleItems,
            this._simpleItems.findIndex((o) => o.id === id)
        );

        utils.reassignItems(this);
        utils.datesItems(this);
    },

    _setMessageDeletedDraft(id) {
        Vue.delete(
            this._simpleItems,
            this._simpleItems.findIndex((o) => o.conversation_id === id)
        );

        useConversationsStore()._setMessageDeletedDraft(id);
    },

    _setMessageUpdated(data) {
        const simpleItem = this._simpleItems.find((item) => item.id === data.id);

        if (simpleItem) {
            simpleItem.update(data);
        }

        if (this._items[data.id]) {
            data.previews = this._items[data.id].previews;

            Vue.set(this._items, data.id, new MessageSimple(data));

            utils.reassignItems(this);
            utils.datesItems(this);
        }
    },

    _setMessageResetStatus(id) {
        if (this._items[id]) {
            this._items[id].update({ status: null });
        }
    },

    _setMessageUpdateSignatureMessage({ status, message }) {
        this._isSignatureActive = status;
        this._signatureMessage = message;
    },
};

export default {
    state,
    getters,
    actions,
};
